/**
 * Named ticket profiles.
 *
 * THE ONE IDEA THIS PANEL HAS TO TEACH: blank means inherit.
 *
 * A profile that fills in only Priority is not half-finished - it is the most
 * useful shape there is, meaning "same as usual, but urgent for this customer".
 * The alternative reading, that every field must be filled, produces profiles
 * that quietly stop tracking the tenant defaults: change the board six months
 * later and the customers on profiles keep filing to the old one, which nobody
 * discovers until somebody asks why their tickets went unanswered.
 *
 * So every field says "same as default" in its empty state, in words, rather
 * than showing a blank box and hoping.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Ref { id: string; name: string }

interface Profile {
  id: string;
  name: string;
  queue: Ref | null;
  status: Ref | null;
  priority: Ref | null;
  ticketType: Ref | null;
  closeStatus: Ref | null;
  grouping: 'device' | 'client' | null;
  subjectTemplate: string | null;
  bodyTemplate: string | null;
}

interface Preview {
  subject: string;
  body: string;
  unknown: string[];
  tokens: Array<{ token: string; describes: string; example: string }>;
}

const FIELDS = [
  ['queue', 'Queue / board'],
  ['status', 'Opening status'],
  ['priority', 'Priority'],
  ['ticketType', 'Ticket type'],
  ['closeStatus', 'Close status'],
] as const;

type FieldKey = (typeof FIELDS)[number][0];

const blank = (): Profile => ({
  id: '', name: '',
  queue: null, status: null, priority: null, ticketType: null, closeStatus: null,
  grouping: null, subjectTemplate: null, bodyTemplate: null,
});

export function TicketProfiles({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [draft, setDraft] = useState<Profile | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  /*
   * Debounced rather than per keystroke. The renderer lives on the server so
   * the preview cannot drift from the real ticket; 300ms makes that a
   * reasonable trade rather than a call per character.
   */
  useEffect(() => {
    if (!draft || !orgId) { setPreview(null); return; }
    const id = setTimeout(() => {
      void (async () => {
        try {
          const r = await httpsCallable(functions, 'previewTicketTemplate')({
            orgId,
            subjectTemplate: draft.subjectTemplate ?? '',
            bodyTemplate: draft.bodyTemplate ?? '',
          });
          setPreview(r.data as Preview);
        } catch {
          // A preview that cannot be fetched simply does not render. It must
          // never block saving - the template is still valid without it.
        }
      })();
    }, 300);
    return () => clearTimeout(id);
  }, [orgId, draft?.subjectTemplate, draft?.bodyTemplate, draft !== null]);

  const load = async () => {
    if (!orgId) return;
    try {
      const r = await httpsCallable(functions, 'listTicketProfiles')({ orgId });
      setProfiles((r.data as { profiles: Profile[] }).profiles ?? []);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { void load(); }, [orgId]);

  async function save() {
    if (!draft || !orgId) return;
    setBusy(true); setErr(null);
    try {
      await httpsCallable(functions, 'saveTicketProfile')({ orgId, ...draft });
      setDraft(null);
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function remove(id: string) {
    if (!orgId) return;
    setBusy(true); setErr(null);
    try {
      await httpsCallable(functions, 'deleteTicketProfile')({ orgId, id });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  if (!orgId) return null;

  const setField = (k: FieldKey, raw: string) => {
    if (!draft) return;
    const v = raw.trim();
    setDraft({ ...draft, [k]: v === '' ? null : { id: v, name: v } });
  };

  return (
    <section className="panel">
      <h2 className="panel-title">Ticket profiles</h2>
      <p className="panel-note" style={{ marginTop: 0 }}>
        A named set of ticket settings you can point a client at &mdash; a contract customer whose
        failures are urgent and land on the managed board, a break-fix customer whose do not.
        Choose one per client on the Policies page.
      </p>
      <p className="panel-note">
        <strong>Leave a field blank to inherit it.</strong> A profile that sets only Priority keeps
        every other setting from the defaults above, and follows them when they change later.
      </p>

      {err && <div className="banner banner-error">{err}</div>}

      <table className="mini-table">
        <thead><tr><th>Profile</th><th>Overrides</th><th /></tr></thead>
        <tbody>
          {profiles.length === 0 && (
            <tr><td colSpan={3} className="dim">
              No profiles. Every client uses the settings above.
            </td></tr>
          )}
          {profiles.map((p) => {
            const set: string[] = FIELDS.filter(([k]) => p[k] !== null).map(([, label]) => label);
            if (p.grouping) set.push(`grouping: ${p.grouping}`);
            return (
              <tr key={p.id}>
                <td style={{ fontWeight: 600 }}>{p.name}</td>
                <td className="dim" style={{ fontSize: 12 }}>
                  {set.length === 0 ? 'nothing yet - same as the defaults' : set.join(', ')}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {canManage && (
                    <>
                      <button className="btn-link" onClick={() => setDraft(p)}>edit</button>
                      <button className="btn-link danger" style={{ marginLeft: 10 }}
                              disabled={busy} onClick={() => void remove(p.id)}>delete</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {canManage && !draft && (
        <button className="btn" style={{ marginTop: 12 }} onClick={() => setDraft(blank())}>
          Add a profile
        </button>
      )}

      {draft && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <label className="field" style={{ maxWidth: 320 }}>
            <span className="field-label">Name</span>
            <input className="field-input" value={draft.name} placeholder="Contract customers"
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>

          <div className="row" style={{ gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            {FIELDS.map(([k, label]) => (
              <label key={k} className="field">
                <span className="field-label">{label}</span>
                <input
                  className="field-input"
                  value={draft[k]?.id ?? ''}
                  placeholder="same as default"
                  onChange={(e) => setField(k, e.target.value)}
                />
              </label>
            ))}
            <label className="field">
              <span className="field-label">Grouping</span>
              <select className="field-input" value={draft.grouping ?? ''}
                      onChange={(e) => setDraft({
                        ...draft,
                        grouping: e.target.value === '' ? null : (e.target.value as 'device' | 'client'),
                      })}>
                <option value="">same as default</option>
                <option value="device">One ticket per machine</option>
                <option value="client">One ticket per client</option>
              </select>
            </label>
          </div>

          <p className="panel-note">
            These are the PSA&rsquo;s own ids. Copy them from the lists on this page &mdash; an id
            the PSA does not recognise is rejected when the ticket is filed, not here.
          </p>

          <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div className="field-label" style={{ marginBottom: 6 }}>Wording</div>
            <label className="field" style={{ maxWidth: 640 }}>
              <span className="field-label">Subject</span>
              <input
                className="field-input"
                value={draft.subjectTemplate ?? ''}
                placeholder="same as default"
                onChange={(e) => setDraft({ ...draft, subjectTemplate: e.target.value || null })}
              />
            </label>
            <label className="field" style={{ maxWidth: 640, marginTop: 8 }}>
              <span className="field-label">Body</span>
              <textarea
                className="field-input" rows={6}
                value={draft.bodyTemplate ?? ''}
                placeholder="same as default"
                onChange={(e) => setDraft({ ...draft, bodyTemplate: e.target.value || null })}
              />
            </label>

            {preview && (
              <div style={{ marginTop: 10 }}>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {preview.tokens.map((t) => (
                    <button
                      key={t.token} type="button" className="btn"
                      title={`${t.describes} - e.g. ${t.example}`}
                      onClick={() => setDraft({
                        ...draft,
                        subjectTemplate: (draft.subjectTemplate ?? '') + `{{${t.token}}}`,
                      })}
                    >{`{{${t.token}}}`}</button>
                  ))}
                </div>

                {preview.unknown.length > 0 && (
                  <div className="banner banner-error" style={{ marginBottom: 8 }}>
                    Not a real token: {preview.unknown.map((u) => `{{${u}}}`).join(', ')}. It will
                    render as nothing rather than appearing in a customer&rsquo;s ticket.
                  </div>
                )}

                {/* The preview comes from the SAME renderer that writes the
                    ticket, through a callable, rather than a copy in the
                    browser. A preview that is a second implementation
                    eventually disagrees with the tickets it promised. */}
                <div className="panel-note" style={{ marginBottom: 4 }}>How a ticket will read:</div>
                <div style={{
                  border: '1px solid var(--border)', borderRadius: 6, padding: 10,
                  background: 'var(--bg-alt, transparent)',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{preview.subject}</div>
                  <pre style={{
                    margin: 0, whiteSpace: 'pre-wrap', fontSize: 12,
                    fontFamily: 'var(--mono)', color: 'var(--text-dim, inherit)',
                  }}>{preview.body}</pre>
                </div>
              </div>
            )}
          </div>

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving' : 'Save profile'}
            </button>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
